import React, { useEffect, useState } from 'react';
import { Card, Button, Form, Spinner, Alert, Row, Col, Badge } from 'react-bootstrap';
import { PaymentMethodService } from '../../../Services/Axios/Api';
import { useState as useReactState } from 'react';

const parseConfig = (configStr, provider) => {
  try {
    const parsed = JSON.parse(configStr || '{}');
    
    // Handle different provider configurations
    if (provider === 'Noon') {
      return {
        liveAppKey: parsed.live?.appKey || '',
        liveBusinessId: parsed.live?.businessId || '',
        liveAppName: parsed.live?.appName || '',
        testAppKey: parsed.test?.appKey || '',
        testBusinessId: parsed.test?.businessId || '',
        testAppName: parsed.test?.appName || '',
        isNoonProvider: true
      };
    } else {
      // Default for Fawry, AhlyMomken, etc.
      return {
        liveMerchantCode: parsed.live?.merchantCode || '',
        liveApiKey: parsed.live?.apiKey || '',
        testMerchantCode: parsed.test?.merchantCode || '',
        testApiKey: parsed.test?.apiKey || '',
        isNoonProvider: false
      };
    }
  } catch {
    if (provider === 'Noon') {
      return { 
        liveAppKey: '', 
        liveBusinessId: '', 
        liveAppName: '', 
        testAppKey: '', 
        testBusinessId: '', 
        testAppName: '', 
        isNoonProvider: true 
      };
    } else {
      return { 
        liveMerchantCode: '', 
        liveApiKey: '', 
        testMerchantCode: '', 
        testApiKey: '', 
        isNoonProvider: false 
      };
    }
  }
};

const combineConfig = (fields) => {
  if (fields.isNoonProvider) {
    return JSON.stringify({
      live: {
        appKey: fields.liveAppKey,
        businessId: fields.liveBusinessId,
        appName: fields.liveAppName,
      },
      test: {
        appKey: fields.testAppKey,
        businessId: fields.testBusinessId,
        appName: fields.testAppName,
      },
    }, null, 2);
  } else {
    return JSON.stringify({
      live: {
        merchantCode: fields.liveMerchantCode,
        apiKey: fields.liveApiKey,
      },
      test: {
        merchantCode: fields.testMerchantCode,
        apiKey: fields.testApiKey,
      },
    }, null, 2);
  }
};

const PaymentInfo = () => {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [successId, setSuccessId] = useState(null);
  const [edit, setEdit] = useState({});
  // Add visibility state for each field per method
  const [showFields, setShowFields] = useReactState({});

  useEffect(() => {
    const fetchMethods = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await PaymentMethodService.getPaymentMethods();
        setMethods(data.result || []);
        setEdit(
          (data.result || []).reduce((acc, m) => {
            const parsed = parseConfig(m.configuration, m.provider);
            acc[m.id] = { ...m, ...parsed };
            return acc;
          }, {})
        );
      } catch (err) {
        if (err.message?.includes('401')) {
          setError('Session expired or unauthorized. Please log out and log back in.');
        } else {
          setError(err.message || 'Failed to load payment methods');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchMethods();
  }, []);

  const handleChange = (id, field, value) => {
    setEdit((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }));
  };

  const handleSave = async (id) => {
    setSavingId(id);
    setSuccessId(null);
    setError(null);
    try {
      const method = methods.find((m) => m.id === id);
      const configStr = combineConfig(edit[id]);
      const updateData = {
        id: method.id,
        tenantId: method.tenantId,
        name: edit[id].name,
        provider: method.provider,
        isEnabled: edit[id].isEnabled,
        configuration: configStr,
        description: method.description,
        currency: edit[id].currency,
        isTestMode: edit[id].isTestMode,
        order: edit[id].order || 0,
      };
      await PaymentMethodService.updatePaymentMethod(id, updateData);
      setSuccessId(id);
    } catch (err) {
      setError(err.message || 'Failed to save payment method');
    } finally {
      setSavingId(null);
    }
  };

  // Toggle visibility for a field
  const toggleField = (id, field) => {
    setShowFields((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: !prev[id]?.[field],
      },
    }));
  };

  if (loading) return <div>Loading payment methods...</div>;
  if (error) return <Alert variant="danger">{error}</Alert>;

  return (
    <div>
      {methods.map((method) => (
        <Card className="mb-3" key={method.id}>
          <Card.Header>
            <Row>
              <Col>
                <div className="d-flex align-items-center gap-2">
                  <b>{method.name}</b>
                  <Badge bg="info" className="text-uppercase">
                    {method.provider}
                  </Badge>
                </div>
              </Col>
              <Col className="text-end">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleSave(method.id)}
                  disabled={savingId === method.id}
                >
                  {savingId === method.id ? <Spinner animation="border" size="sm" className="me-2" /> : null}
                  Save
                </Button>
                {successId === method.id && <span className="text-success ms-2">Saved!</span>}
              </Col>
            </Row>
          </Card.Header>
          <Card.Body>
            <Form>
              <Row className="mb-3">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={edit[method.id]?.name || ''}
                      onChange={(e) => handleChange(method.id, 'name', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Provider</Form.Label>
                    <Form.Control
                      type="text"
                      value={edit[method.id]?.provider || ''}
                      disabled
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Currency</Form.Label>
                    <Form.Control
                      type="text"
                      value={edit[method.id]?.currency || ''}
                      // onChange={(e) => handleChange(method.id, 'currency', e.target.value)}
                      disabled
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Order</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      value={edit[method.id]?.order || 0}
                      onChange={(e) => handleChange(method.id, 'order', parseInt(e.target.value) || 0)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6} className="d-flex align-items-center">
                  <Form.Group>
                    <Form.Label className="me-2">Enabled</Form.Label>
                    <Form.Switch
                      id={`enabled-switch-${method.id}`}
                      checked={edit[method.id]?.isEnabled || false}
                      onChange={(e) => handleChange(method.id, 'isEnabled', e.target.checked)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6} className="d-flex align-items-center">
                  <Form.Group>
                    <Form.Label className="me-2">Test Mode</Form.Label>
                    <Form.Switch
                      id={`testmode-switch-${method.id}`}
                      checked={edit[method.id]?.isTestMode || false}
                      onChange={(e) => handleChange(method.id, 'isTestMode', e.target.checked)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              {/* Live Configuration Fields */}
              <Row className="mb-3">
                {edit[method.id]?.isNoonProvider ? (
                  // Noon Provider Fields
                  <>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Live App Key</Form.Label>
                        <div className="input-group">
                          <Form.Control
                            type={showFields[method.id]?.liveAppKey ? 'text' : 'password'}
                            value={edit[method.id]?.liveAppKey || ''}
                            onChange={(e) => handleChange(method.id, 'liveAppKey', e.target.value)}
                          />
                          <Button
                            variant="outline-secondary"
                            type="button"
                            onClick={() => toggleField(method.id, 'liveAppKey')}
                            tabIndex={-1}
                          >
                            <i className={`fa ${showFields[method.id]?.liveAppKey ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                          </Button>
                        </div>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Live Business ID</Form.Label>
                        <Form.Control
                          type="text"
                          value={edit[method.id]?.liveBusinessId || ''}
                          onChange={(e) => handleChange(method.id, 'liveBusinessId', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Live App Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={edit[method.id]?.liveAppName || ''}
                          onChange={(e) => handleChange(method.id, 'liveAppName', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </>
                ) : (
                  // Standard Provider Fields (Fawry, AhlyMomken, etc.)
                  <>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Live Merchant Code</Form.Label>
                        <div className="input-group">
                          <Form.Control
                            type={showFields[method.id]?.liveMerchantCode ? 'text' : 'password'}
                            value={edit[method.id]?.liveMerchantCode || ''}
                            onChange={(e) => handleChange(method.id, 'liveMerchantCode', e.target.value)}
                          />
                          <Button
                            variant="outline-secondary"
                            type="button"
                            onClick={() => toggleField(method.id, 'liveMerchantCode')}
                            tabIndex={-1}
                          >
                            <i className={`fa ${showFields[method.id]?.liveMerchantCode ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                          </Button>
                        </div>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Live API Key</Form.Label>
                        <div className="input-group">
                          <Form.Control
                            type={showFields[method.id]?.liveApiKey ? 'text' : 'password'}
                            value={edit[method.id]?.liveApiKey || ''}
                            onChange={(e) => handleChange(method.id, 'liveApiKey', e.target.value)}
                          />
                          <Button
                            variant="outline-secondary"
                            type="button"
                            onClick={() => toggleField(method.id, 'liveApiKey')}
                            tabIndex={-1}
                          >
                            <i className={`fa ${showFields[method.id]?.liveApiKey ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                          </Button>
                        </div>
                      </Form.Group>
                    </Col>
                  </>
                )}
              </Row>

              {/* Test Configuration Fields */}
              <Row className="mb-3">
                {edit[method.id]?.isNoonProvider ? (
                  // Noon Provider Test Fields
                  <>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Test App Key</Form.Label>
                        <div className="input-group">
                          <Form.Control
                            type={showFields[method.id]?.testAppKey ? 'text' : 'password'}
                            value={edit[method.id]?.testAppKey || ''}
                            onChange={(e) => handleChange(method.id, 'testAppKey', e.target.value)}
                          />
                          <Button
                            variant="outline-secondary"
                            type="button"
                            onClick={() => toggleField(method.id, 'testAppKey')}
                            tabIndex={-1}
                          >
                            <i className={`fa ${showFields[method.id]?.testAppKey ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                          </Button>
                        </div>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Test Business ID</Form.Label>
                        <Form.Control
                          type="text"
                          value={edit[method.id]?.testBusinessId || ''}
                          onChange={(e) => handleChange(method.id, 'testBusinessId', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Test App Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={edit[method.id]?.testAppName || ''}
                          onChange={(e) => handleChange(method.id, 'testAppName', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </>
                ) : (
                  // Standard Provider Test Fields
                  <>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Test Merchant Code</Form.Label>
                        <div className="input-group">
                          <Form.Control
                            type={showFields[method.id]?.testMerchantCode ? 'text' : 'password'}
                            value={edit[method.id]?.testMerchantCode || ''}
                            onChange={(e) => handleChange(method.id, 'testMerchantCode', e.target.value)}
                          />
                          <Button
                            variant="outline-secondary"
                            type="button"
                            onClick={() => toggleField(method.id, 'testMerchantCode')}
                            tabIndex={-1}
                          >
                            <i className={`fa ${showFields[method.id]?.testMerchantCode ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                          </Button>
                        </div>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Test API Key</Form.Label>
                        <div className="input-group">
                          <Form.Control
                            type={showFields[method.id]?.testApiKey ? 'text' : 'password'}
                            value={edit[method.id]?.testApiKey || ''}
                            onChange={(e) => handleChange(method.id, 'testApiKey', e.target.value)}
                          />
                          <Button
                            variant="outline-secondary"
                            type="button"
                            onClick={() => toggleField(method.id, 'testApiKey')}
                            tabIndex={-1}
                          >
                            <i className={`fa ${showFields[method.id]?.testApiKey ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                          </Button>
                        </div>
                      </Form.Group>
                    </Col>
                  </>
                )}
              </Row>
              <Form.Text className="text-muted">
                <Alert bg="info">
                  <i className="fa fa-info-circle"></i> 
                  {edit[method.id]?.isNoonProvider 
                    ? 'Noon provider uses App Key, Business ID, and App Name fields. These will be stored in the configuration for both live and test mode.'
                    : 'These fields will be stored in the configuration for both live and test mode.'
                  }
                </Alert>
              </Form.Text>
            </Form>
          </Card.Body>
        </Card>
      ))}
    </div>
  );
};

export default PaymentInfo; 